import React, { useEffect, useState } from "react";
import { Card, Grid, Button, Tab, Tabs } from "@mui/material";
import { Add as AddIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { skillActions } from "store/skill-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchAllSkills,
  fetchAllCertifications,
  deleteSkill,
  fetchExpiringCertifications,
} from "app/services/skill.service";
import { SkillsDataGrid } from "./SkillsDataGrid";
import { CertificationsDataGrid } from "./CertificationsDataGrid";
import AddSkillDialog from "./AddSkillDialog";
import AddCertificationDialog from "./AddCertificationDialog";
import { ConfirmDialog } from "../ConfirmDialog";

export const SkillMatrixAdminView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [editSkill, setEditSkill] = useState(null);
  const [editCert, setEditCert] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  const skills = useSelector((state) => state.skill.skills);
  const certifications = useSelector((state) => state.skill.certifications);
  const expiringCertifications = useSelector((state) => state.skill.expiringCertifications);

  useEffect(() => {
    loadSkills();
    loadCertifications();
    loadExpiringCertifications();
  }, []);

  const loadSkills = () => {
    setIsLoading(true);
    fetchAllSkills()
      .then((data) => dispatch(skillActions.setSkills(data)))
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  const loadCertifications = () => {
    fetchAllCertifications()
      .then((data) => dispatch(skillActions.setCertifications(data)))
      .catch((error) => handleAxiosError(error));
  };

  const loadExpiringCertifications = () => {
    fetchExpiringCertifications(30)
      .then((data) => dispatch(skillActions.setExpiringCertifications(data)))
      .catch((error) => handleAxiosError(error));
  };

  const handleEditSkill = (skill) => {
    setEditSkill(skill);
    setSkillDialogOpen(true);
  };

  const handleDeleteSkill = (skill) => {
    setDeleteItem(skill);
    setDeleteType("skill");
    setConfirmOpen(true);
  };

  const handleEditCert = (cert) => {
    setEditCert(cert);
    setCertDialogOpen(true);
  };

  const handleDeleteCert = (cert) => {
    setDeleteItem(cert);
    setDeleteType("certification");
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteType === "skill" && deleteItem) {
      await deleteSkill(deleteItem.id);
      loadSkills();
    }
    setDeleteItem(null);
    setDeleteType("");
  };

  const expertCount = skills.filter((s) => s.isActive).length;

  return (
    <MDBox px={5} py={10}>
      <Grid container spacing={10}>
        {/* Stats Cards */}
        <Grid container spacing={10} item xs={12}>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px"}}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="info">Skills</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{skills.length}</MDTypography>
                <MDTypography variant="caption">Total Skills</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px"}}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="success">Active</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{expertCount}</MDTypography>
                <MDTypography variant="caption">Active Skills</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px"}}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="warning">Certifications</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{certifications.length}</MDTypography>
                <MDTypography variant="caption">Total Certs</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px"}}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="error">Expiring</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{expiringCertifications.length}</MDTypography>
                <MDTypography variant="caption">Expiring in 30 days</MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content with Tabs */}
        <Grid item xs={12} width="100%">
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Skills" />
                <Tab label="Certifications" />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => { setEditSkill(null); setSkillDialogOpen(true); }}
                    variant="contained"
                    color="success"
                  >
                    Add Skill
                  </Button>
                </MDBox>
                <SkillsDataGrid
                  skills={skills}
                  isLoading={isLoading}
                  onEdit={handleEditSkill}
                  onDelete={handleDeleteSkill}
                />
              </MDBox>
            )}
            {tabValue === 1 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => { setEditCert(null); setCertDialogOpen(true); }}
                    variant="contained"
                    color="success"
                  >
                    Add Certification
                  </Button>
                </MDBox>
                <CertificationsDataGrid
                  certifications={certifications}
                  isLoading={isLoading}
                  onEdit={handleEditCert}
                  onDelete={handleDeleteCert}
                />
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>

      <AddSkillDialog
        open={skillDialogOpen}
        setOpen={setSkillDialogOpen}
        onSuccess={loadSkills}
        editObject={editSkill}
      />
      <AddCertificationDialog
        open={certDialogOpen}
        setOpen={setCertDialogOpen}
        onSuccess={loadCertifications}
        editObject={editCert}
      />
      <ConfirmDialog
        title={`Delete ${deleteType}?`}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={confirmDelete}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        Are you sure you want to delete this {deleteType}?
      </ConfirmDialog>
    </MDBox>
  );
};

